import React, { useEffect, useState, useCallback, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { api, apiForm } from "../../api/client";

// Read-only thumbnail that fetches its own signed URL
function MediaThumb({ inspectionId, mediaId, token, caption, onOpen }) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api(
          `/india/inspections/${inspectionId}/media/${mediaId}/download`,
          { token }
        );
        if (!cancelled) setUrl(res?.url || null);
      } catch {
        if (!cancelled) setUrl(null);
      }
    })();
    return () => { cancelled = true; };
  }, [inspectionId, mediaId, token]);

  return (
    <div className="flex flex-col items-center" style={{ width: 120 }}>
      {caption && (
        <span className="text-xs font-semibold mb-1 px-2 py-0.5 rounded"
          style={{
            background: caption === "Finding" ? "#fff3e0" : "#e6f4e6",
            color: caption === "Finding" ? "#8b4a00" : "#2d6a2d",
          }}>
          {caption}
        </span>
      )}
      <div
        onClick={() => url && onOpen?.()}
        style={{
          width: 120, height: 90,
          background: "#f0f4f8",
          borderRadius: 8,
          overflow: "hidden",
          cursor: url ? "pointer" : "default",
          display: "flex", alignItems: "center", justifyContent: "center",
          border: "1px solid #dde6f0",
        }}
      >
        {url ? (
          <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <span className="text-xs text-gray-400">Loading…</span>
        )}
      </div>
    </div>
  );
}

export default function EvidenceUploadPanel() {
  const { inspectionId, token, user, inspection } =
    useOutletContext();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notesByFindingId, setNotesByFindingId] = useState({});
  const [err, setErr] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // finding_id → { finding: [], evidence: [] }
  const [mediaByFinding, setMediaByFinding] = useState({});
  const [uploading, setUploading] = useState({});
  const fileInputRefs = useRef({});

  const isInspector =
    inspection?.inspector_user_id != null &&
    user?.id != null &&
    Number(inspection.inspector_user_id) === Number(user.id);

  const load = useCallback(async () => {
    if (!inspectionId || !token) return;
    setLoading(true);
    setErr(null);
    try {
      const data = await api(
        `/india/inspections/${inspectionId}/findings?status=open`,
        { token }
      );
      const findings = Array.isArray(data.items) ? data.items : [];
      setItems(findings);

      // For each finding, fetch media split by finding_id
      const mediaMap = {};
      await Promise.all(
        findings.map(async (f) => {
          const aid = f.answer_id != null ? Number(f.answer_id) : null;
          if (!aid) {
            mediaMap[f.id] = { finding: [], evidence: [] };
            return;
          }
          try {
            const res = await api(
              `/india/inspections/${inspectionId}/answers/${aid}/media`,
              { token }
            );
            const all = Array.isArray(res.items) ? res.items : [];
            mediaMap[f.id] = {
              finding: all.filter((m) => m.finding_id == null),
              evidence: all.filter((m) => Number(m.finding_id) === Number(f.id)),
            };
          } catch {
            mediaMap[f.id] = { finding: [], evidence: [] };
          }
        })
      );
      setMediaByFinding(mediaMap);
    } catch (e) {
      setErr(e?.message || "Failed to load findings");
      setItems([]);
      setMediaByFinding({});
    } finally {
      setLoading(false);
    }
  }, [inspectionId, token]);

  useEffect(() => {
    load();
  }, [load]);

  async function uploadEvidence(findingId, answerId, file) {
    if (!file) return;
    setUploading((prev) => ({ ...prev, [findingId]: true }));
    setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("answer_id", String(answerId));
      fd.append("finding_id", String(findingId));
      await apiForm(`/india/inspections/${inspectionId}/media`, fd, { token });
      await load();
    } catch (e) {
      setErr(e?.message || "Upload failed");
    } finally {
      setUploading((prev) => ({ ...prev, [findingId]: false }));
    }
  }

  async function openMedia(mediaId) {
    try {
      const res = await api(
        `/india/inspections/${inspectionId}/media/${mediaId}/download`,
        { token }
      );
      if (res?.url) window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setErr(e?.message || "Failed to open media");
    }
  }

  async function deleteMedia(mediaId, findingId) {
    if (!window.confirm("Delete this evidence?")) return;
    setErr(null);
    try {
      await api(`/india/inspections/${inspectionId}/media/${mediaId}`, {
        token,
        method: "DELETE",
      });
      await load();
    } catch (e) {
      setErr(e?.message || "Delete failed");
    }
  }

  async function submitEvidenceForFinding(findingId) {
    await api(
      `/india/inspections/${inspectionId}/findings/${findingId}/submit-evidence`,
      { token, method: "PATCH" }
    );
  }

  async function submitAllEvidence() {
    setErr(null);
    setSubmitting(true);
    try {
      const eligible = items.filter(
        (f) => (mediaByFinding[f.id]?.evidence || []).length > 0
      );
      if (eligible.length === 0) {
        setErr("No open points have close-out evidence yet. Upload files first.");
        return;
      }
      for (const f of eligible) {
        await submitEvidenceForFinding(f.id);
      }
      await load();
    } catch (e) {
      setErr(e?.message || "Submit evidence failed");
    } finally {
      setSubmitting(false);
    }
  }

  const eligibleCount = items.filter(
    (f) => (mediaByFinding[f.id]?.evidence || []).length > 0
  ).length;

  const canSubmitEvidence = eligibleCount > 0 && !loading;

  return (
    <div className="rounded-xl border bg-white p-4">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div>
          <div className="text-sm font-semibold">Open points</div>
          <div className="text-xs text-gray-600">
            {isInspector
              ? "Inspector view — upload close-out evidence or close findings directly."
              : "Upload close-out evidence for each open point, then submit."}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canSubmitEvidence && (
            <button
              type="button"
              onClick={submitAllEvidence}
              disabled={submitting}
              className="text-sm px-3 py-1 rounded text-white bg-petrol hover:opacity-90 disabled:opacity-60"
            >
              {submitting ? "Submitting…" : `Submit evidence (${eligibleCount})`}
            </button>
          )}
          <button
            type="button"
            onClick={load}
            className="text-sm px-3 py-1 border rounded hover:bg-gray-50"
            disabled={loading}
          >
            Refresh
          </button>
        </div>
      </div>

      {err && (
        <div className="text-sm text-red-700 mb-2 whitespace-pre-wrap">{String(err)}</div>
      )}
      {loading && <div className="text-sm text-gray-600">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="text-sm text-gray-600">Nothing here - review in progress.</div>
      )}

      <div className="space-y-4">
        {items.map((f) => {
          const answerId = f.answer_id != null ? Number(f.answer_id) : null;
          const findingMedia = mediaByFinding[f.id] || { finding: [], evidence: [] };
          const hasEvidence = findingMedia.evidence.length > 0;

          return (
            <div key={f.id} className="rounded-lg border p-3 space-y-3">
              {/* Finding header */}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium">{f.title || `Finding #${f.id}`}</div>
                  {f.description && (
                    <div className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">
                      {f.description}
                    </div>
                  )}
                  <div className="text-xs text-gray-500 mt-1">
                    Severity: {f.severity || "n/a"} · Finding ID: {f.id}
                  </div>
                </div>
              </div>

              {/* Photos row — finding photos (read-only) + evidence photos (with delete) */}
              {(findingMedia.finding.length > 0 || findingMedia.evidence.length > 0) && (
                <div className="flex flex-wrap gap-3 pt-1">
                  {findingMedia.finding.map((m) => (
                    <MediaThumb
                      key={m.id}
                      inspectionId={inspectionId}
                      mediaId={m.id}
                      token={token}
                      caption="Finding"
                      onOpen={() => openMedia(m.id)}
                    />
                  ))}
                  {findingMedia.evidence.map((m) => (
                    <div key={m.id} className="flex flex-col items-center gap-1">
                      <MediaThumb
                        inspectionId={inspectionId}
                        mediaId={m.id}
                        token={token}
                        caption="Close-out"
                        onOpen={() => openMedia(m.id)}
                      />
                      <button
                        type="button"
                        onClick={() => deleteMedia(m.id, f.id)}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload close-out evidence */}
              {answerId && (
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    multiple
                    style={{ display: "none" }}
                    ref={(el) => { fileInputRefs.current[f.id] = el; }}
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      for (const file of files) {
                        await uploadEvidence(f.id, answerId, file);
                      }
                      e.target.value = "";
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRefs.current[f.id]?.click()}
                    disabled={uploading[f.id]}
                    className="text-sm px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-60"
                  >
                    {uploading[f.id]
                      ? "Uploading…"
                      : hasEvidence
                      ? "Add more evidence"
                      : "Upload close-out evidence"}
                  </button>
                </div>
              )}

              {/* Inspector verification notes */}
              {isInspector && (
                <div>
                  <div className="text-xs font-semibold mb-1">Verification notes</div>
                  <textarea
                    className="w-full border rounded px-3 py-2 text-sm"
                    rows={2}
                    placeholder="What was checked / evidence reviewed / fix verified…"
                    value={notesByFindingId[f.id] || ""}
                    onChange={(e) =>
                      setNotesByFindingId((prev) => ({ ...prev, [f.id]: e.target.value }))
                    }
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="text-xs text-gray-500">
          Status: {inspection?.status || "unknown"} · Open findings: {items.length}
        </div>
      </div>
    </div>
  );
}